/**
 * @fileoverview This file contains the canonical schema for a personality profile,
 * used for character creation inspiration.
 */
import { z } from 'zod';
import {
  SHAPE_DEFINITIONS,
  ATHLETICISM_DEFINITIONS,
  SKINTONE_DEFINITIONS,
  CHEST_DEFINITIONS,
  WAIST_DEFINITIONS,
  HIPS_DEFINITIONS,
  EYE_COLOR_OPTIONS,
  HAIR_COLOR_OPTIONS,
  BODY_ARCHETYPE_NAMES,
  CHARACTER_ARCHETYPE_NAMES,
  ETHNICITY_L1_NAMES,
} from '../constants';

// Step 1: Define the Zod schema as the single source of truth, making it as specific as possible.
export const PersonProfileSchema = z.object({
  id: z.string(),
  name: z.string(),
  age: z.number(),
  gender: z.enum(['Female', 'Male', 'Non-binary']),
  profession: z.string(),
  height: z.number(),
  // Use z.enum to enforce specific string values, just like the original interface.
  shape: z.enum(Object.keys(SHAPE_DEFINITIONS) as [string, ...string[]]),
  athleticism: z.enum(Object.keys(ATHLETICISM_DEFINITIONS) as [string, ...string[]]),
  chest: z.enum(Object.keys(CHEST_DEFINITIONS) as [string, ...string[]]),
  waist: z.enum(Object.keys(WAIST_DEFINITIONS) as [string, ...string[]]),
  hips: z.enum(Object.keys(HIPS_DEFINITIONS) as [string, ...string[]]),
  bodyDescription: z.string(),
  personalityDescription: z.array(z.string()),
  faceDescription: z.string(),
  skinTone: z.enum(Object.keys(SKINTONE_DEFINITIONS) as [string, ...string[]]),
  hairColor: z.enum(HAIR_COLOR_OPTIONS as unknown as [string, ...string[]]),
  eyeColor: z.enum(EYE_COLOR_OPTIONS as unknown as [string, ...string[]]),
  ethnicities: z.record(z.enum(ETHNICITY_L1_NAMES), z.array(z.string())),
  nationalities: z.array(z.string()),
  characterArchetype: z.array(z.enum(CHARACTER_ARCHETYPE_NAMES as unknown as [string, ...string[]])),
  bodyArchetype: z.enum(BODY_ARCHETYPE_NAMES as unknown as [string, ...string[]]),
  mainImageId: z.string().optional(),
  personalityDescriptionV2: z.string().optional(),
});

// Step 2: Derive the TypeScript type directly from the Zod schema.
// The interface is no longer needed.
export type PersonDataProfile = z.infer<typeof PersonProfileSchema>;


// This schema is used for the output of the personality matcher agent.
export const PersonProfileResultSchema = z.object({
  id: z.string(),
  name: z.string(),
  age: z.number(),
  profession: z.string(),
  height: z.number(),
  shape: z.string(),
  athleticism: z.string(),
});
